// Folding many priced sessions into ONE total, without the total lying about
// itself — the aggregate counterpart to `price-basis.ts` beside this file.
//
// THE TWO DEFECTS THIS EXISTS FOR (arc 101 slice 12 §A2). `PriceBasis` made a
// SINGLE row's dollars traceable. It did nothing for a SUM, and a sum is what
// "how is developerz.ai doing?" asks for. Measured on prod 2026-08-31, over all
// 26,317 `agent_sessions` rows:
//
//   price_basis          sessions      tokens        sum(coalesce(cost_usd,0))
//   legacy_unattributed    18,679   1.52 B                        $2,008.70
//   registry                4,216   0.68 B                          $205.29
//   not_recorded            2,789        0                            $0.00
//   provider_fallback         290   0.07 B                            $2.76
//   unpriced                  282   1.24 B                            $0.00
//   flat_rate                  61   236 K                             $0.00
//
// 1. **`$0.00` means four different things and one of them is correct.** For
//    `flat_rate` it is a PRICE — a coding-plan subscription already paid, so the
//    marginal token costs nothing by contract. For `unpriced` it is `cost_usd IS
//    NULL` collapsed by a `coalesce`, over 1.24 BILLION tokens the platform
//    genuinely cannot price. For `not_recorded` it is a placeholder. An advisor
//    that reads any of those as "free" recommends spending against a balance it
//    cannot see, and one that reads `flat_rate` as "broken telemetry" tells a
//    customer to fix a subscription that is working. Both directions are wrong,
//    which is why {@link UsageRollup} states the flat-rate share as a NUMBER
//    rather than leaving it to be inferred from a zero.
// 2. **A total that covers half the population reads as complete.** 12,070 of
//    those 26,317 sessions (45.9%) report no tokens at all, and only 4,277
//    (16.3%) carry a cost on a basis {@link isDisplayableCost} admits. A bare
//    "$205.29" is not wrong so much as unanswerable — the reader cannot tell it
//    from a $205.29 that saw everything. So every total here ships with the
//    population it was computed over, and the rows it excluded are named
//    individually rather than summed into an "other".
//
// WHAT IS DELIBERATELY ABSENT: a dollar figure for the uncounted rows. Those
// dollars exist in the column ($2,008.70 of `legacy_unattributed` and $2.76 of
// `provider_fallback` above), and `price-basis.ts` is explicit that a ceiling
// "is not a price and must never be rendered as one" while an unattributed row
// is one "nobody can account for". Carrying their SESSIONS and TOKENS says how
// much is missing; carrying their dollars would re-introduce the exact number
// the basis column exists to keep out of a customer's hands.

import {
  type DisplayablePriceBasis,
  isDisplayableCost,
  PriceBasis,
  weakestPriceBasis,
} from './price-basis';

/** A basis whose figure a total may not contain — the complement of the allow-list. */
type UncountedBasis = Exclude<PriceBasis, DisplayablePriceBasis>;

/**
 * One `(model, price_basis)` group as the database returns it — the input this
 * module folds.
 *
 * `costUsd` is the group's `sum(cost_usd)` with SQL's null-skipping already
 * coalesced to 0 by the reader. That zero is safe here and only here, because
 * the only groups whose cost is null are `unpriced`, which {@link rollUpUsage}
 * never adds to a money total.
 */
export type UsageTally = {
  /** The `agent_sessions.model` id, verbatim — never normalised or aliased. */
  model: string;
  basis: PriceBasis;
  sessions: number;
  /** Sessions in this group reporting `token_in + token_out > 0`. */
  reportedSessions: number;
  tokenIn: number;
  tokenOut: number;
  costUsd: number;
};

/**
 * "This number was computed over `covered` of `of` sessions."
 *
 * BOTH numbers, never a percentage: a ratio cannot be re-based by a caller that
 * wants to fold two windows, and a rounded one hides the difference between
 * 0/0 and 1/10,000. `covered === of` is the only shape that means "complete",
 * and it is checkable rather than asserted.
 */
export type UsageCoverage = { covered: number; of: number };

/** A population the money total left out, and the reason it is not money. */
export type UsageUncounted = {
  basis: UncountedBasis;
  sessions: number;
  tokens: number;
  /** Why this basis cannot be counted as spend. */
  why: string;
};

/** A model's own usage, carrying the same two honesty claims as the total. */
export type UsageByModel = {
  model: string;
  sessions: number;
  tokens: number;
  /**
   * Spend on {@link isDisplayableCost} bases only, so a model whose rows are all
   * `unpriced` reads `0` with `pricedSessions: 0` — distinguishable from a
   * flat-rate model's `0` with `pricedSessions === sessions`.
   */
  costUsd: number;
  /**
   * The WEAKEST basis contributing to this model's rows — including the ones
   * excluded from `costUsd`. It describes the population, not the sum: a model
   * priced entirely off ceilings must not read `not_recorded` just because
   * nothing survived the display filter.
   */
  basis: PriceBasis;
  pricedSessions: number;
};

/** The composed usage answer: totals, what they cover, and what they omit. */
export type UsageRollup = {
  sessions: number;
  tokens: {
    in: number;
    out: number;
    /** Sessions that reported any tokens, over every session in the window. */
    coverage: UsageCoverage;
  };
  cost: {
    /** Sum over {@link isDisplayableCost} bases only. */
    usd: number;
    /** Weakest basis contributing to `usd`; `not_recorded` when nothing did. */
    basis: PriceBasis;
    coverage: UsageCoverage;
    /**
     * **Honesty rule 1, stated positively.** Sessions inside `coverage.covered`
     * whose $0 is a flat-rate contract rather than an absence. `flatRateSessions
     * === coverage.covered` with `usd === 0` is a fully-explained zero.
     */
    flatRateSessions: number;
  };
  /** Every excluded basis, largest first. Empty ⇒ `cost.coverage` is complete. */
  uncounted: UsageUncounted[];
  byModel: UsageByModel[];
  /**
   * **Honesty rule 2, applied to the breakdown itself.** `byModel` is capped at
   * {@link USAGE_MODEL_LIMIT}; the totals above are always over ALL models, so a
   * truncated breakdown never truncates a total.
   */
  models: UsageCoverage;
};

/**
 * How many models the breakdown carries. A cap, because `byModel` rides in an
 * MCP answer and an unbounded array there is a shape nobody can budget for; 10
 * because prod's busiest account runs 22 distinct model ids all-time and the
 * tail past 10 is single-digit sessions each.
 *
 * The cap is REPORTED (`models`), never silent — truncation that reads as
 * completeness is the same defect this whole module is about.
 */
export const USAGE_MODEL_LIMIT = 10;

/**
 * Why each excluded basis is not spend, in the words a reader needs in order not
 * to guess — because "excluded" with no reason is the same silence the exclusion
 * exists to break.
 *
 * `Record<UncountedBasis, string>` is the whole guard: a member added to
 * `PriceBasis` that {@link isDisplayableCost} refuses lands in `UncountedBasis`
 * and this record stops compiling, so an unexplained exclusion is not shippable.
 * The `satisfies` keeps that check while leaving the values literal.
 */
const UNCOUNTED_REASON = {
  [PriceBasis.ProviderFallback]:
    'a deliberately-high fail-safe CEILING booked when the model had no rate card — safe for stopping a run, fabricated as a price',
  [PriceBasis.Unpriced]:
    'tokens were spent and this platform has no rate card for the model — the cost is unknown, not zero',
  [PriceBasis.NotRecorded]:
    'no spend recorded: a session not yet settled, a synthetic audit parent that runs no model, or a box that has not reported usage',
  [PriceBasis.LegacyUnattributed]:
    'written before the provenance column existed; how the figure was derived is unknown and will not be guessed',
} satisfies Record<UncountedBasis, string>;

/**
 * Fold `(model, basis)` groups into one self-describing usage answer.
 *
 * PURE and total: an empty input is a real state (a fresh account in a fresh
 * window) and answers zeros with `coverage: {covered: 0, of: 0}` — "nothing
 * happened", which is a different claim from "nothing was counted" and is the
 * one the numbers support.
 *
 * The money total sums {@link isDisplayableCost} bases ONLY. That is the
 * load-bearing choice and it is deliberately lossy: on prod's platform-wide
 * numbers it reports $205.29 rather than the column's $2,216.75, because
 * $2,008.70 of the difference is `legacy_unattributed` — rows nobody can account
 * for — and $2.76 is a fail-safe ceiling. The excluded populations are returned
 * in `uncounted` with their sessions and tokens, so the answer says how much it
 * cannot price instead of quietly pricing it.
 *
 * `basis` folds through {@link weakestPriceBasis}, whose contract ("a total
 * containing one estimate IS an estimate") is stated per-session there and is
 * the same claim across sessions: a window mixing `registry` and `flat_rate`
 * rows grades `flat_rate`, the weaker of the two.
 */
export function rollUpUsage(tallies: readonly UsageTally[]): UsageRollup {
  let sessions = 0;
  let reported = 0;
  let tokenIn = 0;
  let tokenOut = 0;
  let costUsd = 0;
  let pricedSessions = 0;
  let flatRateSessions = 0;
  const costBases: PriceBasis[] = [];
  const uncounted = new Map<UncountedBasis, { sessions: number; tokens: number }>();
  // `basis` is deliberately absent from the accumulator: it is not summable, it
  // is FOLDED from `bases` once the model's groups are all in. Seeding it with a
  // placeholder that every row then overwrites is how a placeholder ships.
  const byModel = new Map<string, Omit<UsageByModel, 'basis'> & { bases: PriceBasis[] }>();

  for (const t of tallies) {
    const tokens = t.tokenIn + t.tokenOut;
    sessions += t.sessions;
    reported += t.reportedSessions;
    tokenIn += t.tokenIn;
    tokenOut += t.tokenOut;

    const model = byModel.get(t.model) ?? {
      model: t.model,
      sessions: 0,
      tokens: 0,
      costUsd: 0,
      pricedSessions: 0,
      bases: [] as PriceBasis[],
    };
    model.sessions += t.sessions;
    model.tokens += tokens;
    model.bases.push(t.basis);
    byModel.set(t.model, model);

    // The predicate is called INLINE in the condition rather than through a
    // stored boolean: the `else` arm needs `t.basis` narrowed to
    // {@link UncountedBasis}, which is what makes the reason lookup total.
    if (isDisplayableCost(t.basis)) {
      costUsd += t.costUsd;
      pricedSessions += t.sessions;
      costBases.push(t.basis);
      if (t.basis === PriceBasis.FlatRate) flatRateSessions += t.sessions;
      model.costUsd += t.costUsd;
      model.pricedSessions += t.sessions;
    } else {
      const prior = uncounted.get(t.basis) ?? { sessions: 0, tokens: 0 };
      uncounted.set(t.basis, {
        sessions: prior.sessions + t.sessions,
        tokens: prior.tokens + tokens,
      });
    }
  }

  const models = [...byModel.values()]
    // Busiest first, then by model id — a stable order, so two calls over an
    // unchanged window cannot disagree about WHICH models the cap dropped.
    .sort((a, b) => b.sessions - a.sessions || a.model.localeCompare(b.model))
    .map(({ bases, ...m }): UsageByModel => ({ ...m, basis: weakestPriceBasis(bases) }));

  return {
    sessions,
    tokens: { in: tokenIn, out: tokenOut, coverage: { covered: reported, of: sessions } },
    cost: {
      usd: costUsd,
      basis: weakestPriceBasis(costBases),
      coverage: { covered: pricedSessions, of: sessions },
      flatRateSessions,
    },
    uncounted: [...uncounted.entries()]
      .map(([basis, v]) => ({ basis, ...v, why: UNCOUNTED_REASON[basis] }))
      .sort((a, b) => b.sessions - a.sessions || a.basis.localeCompare(b.basis)),
    byModel: models.slice(0, USAGE_MODEL_LIMIT),
    models: { covered: Math.min(models.length, USAGE_MODEL_LIMIT), of: models.length },
  };
}
